import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../../../utils/axiosInstance';
import './VaccineManagerModal.css';
import { RequiredVaccinations, isVaccinationUpToDate, getVaccinationsStatus } from '../../../helpers/vaccinationHelper';

function VaccineManagerModal({ isOpen, onClose, dog }) {
  const overlayRef = useRef(null);
  const dateInputRef = useRef(null);
  const bulkDateInputRef = useRef(null);
  const bulkAddDateInputRef = useRef(null);
  const singleVaccineErrorRef = useRef(null);
  const bulkUpdateErrorRef = useRef(null);
  const bulkAddErrorRef = useRef(null);
  const bulkUpdateSectionRef = useRef(null);
  const bulkAddSectionRef = useRef(null);
  const singleFormRef = useRef(null);

  const [selectedVaccine, setSelectedVaccine] = useState('');
  const [date, setDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [bulkDateError, setBulkDateError] = useState('');
  const [vaccinations, setVaccinations] = useState([]);
  const [availableVaccines, setAvailableVaccines] = useState([]);
  const [bulkUpdateMode, setBulkUpdateMode] = useState(false);
  const [bulkAddMode, setBulkAddMode] = useState(false);
  const [selectedVaccinations, setSelectedVaccinations] = useState([]);
  const [selectedVaccinationsToAdd, setSelectedVaccinationsToAdd] = useState([]);
  const [newVaccinationDate, setNewVaccinationDate] = useState('');
  const [isInitialRabies, setIsInitialRabies] = useState(true);
  
  // Reminder date states
  const [reminderMonths, setReminderMonths] = useState(''); // For single add
  const [bulkReminderMode, setBulkReminderMode] = useState('none'); // 'none', 'same', 'different'
  const [sharedReminderMonths, setSharedReminderMonths] = useState(''); // For bulk "same for all"
  const [individualReminderMonths, setIndividualReminderMonths] = useState({}); // For bulk "different"
  const [bulkAddReminderMonths, setBulkAddReminderMonths] = useState(''); // For bulk add

  useEffect(() => {
    if (dog && isOpen) {
      fetchVaccinations();
    }
  }, [dog, isOpen]);

  const fetchVaccinations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get(`/vaccinations/dogs/${dog._id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const fetchedVaccinations = response.data || [];
      setVaccinations(fetchedVaccinations);
      filterAvailableVaccines(fetchedVaccinations);
    } catch (error) {
      // Error handling without console.error
    }
  };

  const filterAvailableVaccines = (vaccinations) => {
    // Check for valid Rabies vaccinations
    const hasValidInitialRabies = vaccinations.some(v => 
      v.name === 'Rabies [Initial]' && isVaccinationUpToDate(v)
    );
    const hasValidContinuingRabies = vaccinations.some(v => 
      v.name === 'Rabies [Continuing]' && isVaccinationUpToDate(v)
    );

    const available = RequiredVaccinations.filter(vaccine => {
      // Special handling for Rabies
      if (vaccine.name === 'Rabies [Initial]' || vaccine.name === 'Rabies [Continuing]') {
        // If continuing exists, only show continuing
        if (hasValidContinuingRabies) {
          return vaccine.name === 'Rabies [Continuing]';
        }
        // If no rabies exists or only initial exists, show both
        return true;
      }

      // For all other vaccines, show them in the dropdown regardless of status
      return true;
    });

    setAvailableVaccines(available);
  };

  // Helper function to calculate reminder date
  const calculateReminderDate = (expirationDate, monthsBefore) => {
    if (!monthsBefore || monthsBefore === '') return null;
    const reminderDate = new Date(expirationDate);
    reminderDate.setMonth(reminderDate.getMonth() - parseInt(monthsBefore));
    return reminderDate.toISOString();
  };

  const handleClickOutside = event => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBulkUpdateStart = () => {
    setBulkUpdateMode(true);
    setBulkAddMode(false);
    setSelectedVaccinations([]);
    setNewVaccinationDate('');
    setBulkReminderMode('none');
    setSharedReminderMonths('');
    setIndividualReminderMonths({});
    
    // Scroll to bulk update section after state updates
    setTimeout(() => {
      if (bulkUpdateSectionRef.current && overlayRef.current) {
        bulkUpdateSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleBulkUpdateCancel = () => {
    setBulkUpdateMode(false);
    setSelectedVaccinations([]);
    setNewVaccinationDate('');
    setBulkReminderMode('none');
    setSharedReminderMonths('');
    setIndividualReminderMonths({});
  };

  const handleVaccinationSelect = (vaccinationId) => {
    setSelectedVaccinations(prev => {
      if (prev.includes(vaccinationId)) {
        return prev.filter(id => id !== vaccinationId);
      }
      return [...prev, vaccinationId];
    });
  };

  // Add useEffect to handle scrolling when bulkDateError changes in bulk update mode
  useEffect(() => {
    if (bulkDateError && bulkUpdateMode) {
      setTimeout(() => {
        scrollToError(bulkUpdateErrorRef, bulkDateInputRef);
      }, 100);
    }
  }, [bulkDateError, bulkUpdateMode]);

  // Add useEffect to handle scrolling when bulkDateError changes in bulk add mode
  useEffect(() => {
    if (bulkDateError && bulkAddMode) {
      setTimeout(() => {
        scrollToError(bulkAddErrorRef, bulkAddDateInputRef);
      }, 100);
    }
  }, [bulkDateError, bulkAddMode]);

  // Add useEffect to handle scrolling when dateError changes in single vaccine mode
  useEffect(() => {
    if (dateError && !bulkUpdateMode && !bulkAddMode) {
      setTimeout(() => {
        scrollToError(singleVaccineErrorRef, dateInputRef);
      }, 100);
    }
  }, [dateError, bulkUpdateMode, bulkAddMode]);

  const scrollToError = (errorRef, inputRef) => {
    const modalContainer = overlayRef.current;
    if (modalContainer && errorRef?.current && inputRef?.current) {
      modalContainer.scrollTo({
        top: inputRef.current.offsetTop - 40, // Scroll to show the input field with some padding
        behavior: 'smooth'
      });
    }
  };

  const handleBulkUpdateSubmit = async () => {
    if (!newVaccinationDate || selectedVaccinations.length === 0) {
      setBulkDateError('Please select vaccinations and enter a date');
      return;
    }

    const selectedDate = new Date(newVaccinationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setBulkDateError('Vaccination date cannot be in the future. Please select today or a past date.');
      return;
    }

    setBulkDateError('');

    try {
      const token = localStorage.getItem('token');
      const updates = selectedVaccinations.map(vaccinationId => {
        const vaccination = vaccinations.find(v => v._id === vaccinationId);
        const vaccinationType = RequiredVaccinations.find(v => v.name === vaccination.name);
        
        if (!vaccinationType) {
          throw new Error(`Unknown vaccination type: ${vaccination.name}`);
        }

        const newExpirationDate = new Date(newVaccinationDate);
        newExpirationDate.setFullYear(newExpirationDate.getFullYear() + vaccinationType.expiration);

        let reminderDate = null;
        if (bulkReminderMode === 'same' && sharedReminderMonths) {
          reminderDate = calculateReminderDate(newExpirationDate.toISOString(), sharedReminderMonths);
        } else if (bulkReminderMode === 'different' && individualReminderMonths[vaccinationId]) {
          reminderDate = calculateReminderDate(newExpirationDate.toISOString(), individualReminderMonths[vaccinationId]);
        }

        return {
          vaccinationId,
          date: newVaccinationDate,
          expirationDate: newExpirationDate.toISOString(),
          reminderDate: reminderDate
        };
      });

      await axiosInstance.post(`/vaccinations/user/bulk-update/${dog._id}`, {
        updates
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const response = await axiosInstance.get(`/vaccinations/dogs/${dog._id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setVaccinations(response.data);
      setBulkUpdateMode(false);
      setSelectedVaccinations([]);
      setNewVaccinationDate('');
      setBulkReminderMode('none');
      setSharedReminderMonths('');
      setIndividualReminderMonths({});
    } catch (error) {
      // Error handling without console.error
    }
  };

  const handleBulkAddStart = () => {
    setBulkAddMode(true);
    setBulkUpdateMode(false);
    setSelectedVaccinationsToAdd([]);
    setNewVaccinationDate('');
    setIsInitialRabies(true);
    setBulkAddReminderMonths('');
    
    // Scroll to bulk add section after state updates
    setTimeout(() => {
      if (bulkAddSectionRef.current && overlayRef.current) {
        bulkAddSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleBulkAddCancel = () => {
    setBulkAddMode(false);
    setSelectedVaccinationsToAdd([]);
    setNewVaccinationDate('');
    setIsInitialRabies(true);
    setBulkAddReminderMonths('');
  };

  const handleVaccinationToAddSelect = (vaccinationName) => {
    setSelectedVaccinationsToAdd(prev => {
      if (prev.includes(vaccinationName)) {
        return prev.filter(name => name !== vaccinationName);
      }
      return [...prev, vaccinationName];
    });
  };

  const handleBulkAddSubmit = async () => {
    if (!newVaccinationDate || selectedVaccinationsToAdd.length === 0) {
      setBulkDateError('Please select vaccinations and enter a date');
      return;
    }

    const selectedDate = new Date(newVaccinationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setBulkDateError('Vaccination date cannot be in the future. Please select today or a past date.');
      return;
    }

    setBulkDateError('');

    try {
      const token = localStorage.getItem('token');
      const newVaccinations = selectedVaccinationsToAdd.map(vaccinationName => {
        // Handle Rabies vaccination type
        let finalVaccinationName = vaccinationName;
        if (vaccinationName === 'Rabies') {
          finalVaccinationName = isInitialRabies ? 'Rabies [Initial]' : 'Rabies [Continuing]';
        }

        const vaccinationType = RequiredVaccinations.find(v => v.name === finalVaccinationName);
        
        if (!vaccinationType) {
          throw new Error(`Unknown vaccination type: ${finalVaccinationName}`);
        }

        const expirationDate = new Date(newVaccinationDate);
        expirationDate.setFullYear(expirationDate.getFullYear() + vaccinationType.expiration);

        const reminderDate = calculateReminderDate(expirationDate.toISOString(), bulkAddReminderMonths);

        return {
          dog: dog._id,
          name: finalVaccinationName,
          date: newVaccinationDate,
          expirationDate: expirationDate.toISOString(),
          verifiedByEmployee: false,
          reminderDate: reminderDate
        };
      });

      await Promise.all(newVaccinations.map(vaccination => 
        axiosInstance.post('/vaccinations/user', vaccination, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ));

      const response = await axiosInstance.get(`/vaccinations/dogs/${dog._id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setVaccinations(response.data);
      setBulkAddMode(false);
      setSelectedVaccinationsToAdd([]);
      setNewVaccinationDate('');
      setIsInitialRabies(true);
      setBulkAddReminderMonths('');
    } catch (error) {
      // Error handling without console.error
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Trim and validate inputs
    const trimmedDate = date.trim();
    
    if (!trimmedDate) {
      setDateError('Please select a date');
      return;
    }

    const selectedDate = new Date(trimmedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setDateError('Vaccination date cannot be in the future');
      return;
    }

    if (!selectedVaccine) {
      setDateError('Please select a vaccination type');
      return;
    }

    setDateError('');

    try {
      const token = localStorage.getItem('token');
      const vaccinationType = RequiredVaccinations.find(v => v.name === selectedVaccine);
      
      if (!vaccinationType) {
        throw new Error(`Unknown vaccination type: ${selectedVaccine}`);
      }

      const expirationDate = new Date(selectedDate);
      expirationDate.setFullYear(expirationDate.getFullYear() + vaccinationType.expiration);

      const reminderDate = calculateReminderDate(expirationDate.toISOString(), reminderMonths);

      await axiosInstance.post(`/vaccinations/${dog._id}`, {
        name: selectedVaccine,
        date: trimmedDate,
        expirationDate: expirationDate.toISOString(),
        reminderDate: reminderDate
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const response = await axiosInstance.get(`/vaccinations/dogs/${dog._id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setVaccinations(response.data);
      setSelectedVaccine('');
      setDate('');
      setReminderMonths('');
    } catch (error) {
      setDateError('Failed to add vaccination. Please try again.');
    }
  };

  const { missingVaccinations, expiredVaccinations, unverifiedVaccinations } = getVaccinationsStatus(vaccinations, RequiredVaccinations);
  
  const isRabiesVaccination = (vacc) => vacc === 'Rabies [Initial]' || vacc === 'Rabies [Continuing]';
  
  const hasValidRabies = vaccinations?.some(v => 
    (v.name === 'Rabies [Initial]' || v.name === 'Rabies [Continuing]') && 
    isVaccinationUpToDate(v)
  );

  const effectiveExpiredVaccinations = hasValidRabies 
    ? expiredVaccinations.filter(v => {
        if (isRabiesVaccination(v)) return false;
        const vaccType = RequiredVaccinations.find(rv => rv.name === v);
        return vaccType?.core === true;
      })
    : expiredVaccinations.filter(v => {
        const vaccType = RequiredVaccinations.find(rv => rv.name === v);
        return vaccType?.core === true;
      });

  const effectiveMissingVaccinations = missingVaccinations.filter(v => {
    const vaccType = RequiredVaccinations.find(rv => rv.name === v);
    return vaccType?.core === true;
  });

  const isUpToDate = effectiveMissingVaccinations.length === 0 && effectiveExpiredVaccinations.length === 0;
  const areAllVaccinationsVerified = unverifiedVaccinations.length === 0 && effectiveMissingVaccinations.length === 0;

  if (!isOpen) return null;

  return (
    <div className="overlay">
      <div ref={overlayRef} className="vaccine-manager-modal-container">
        <button className="vaccine-manager-close" onClick={onClose}>X</button>
        <h2>Manage Vaccinations for {dog.name}</h2>
        
        <div className="vaccination-status">
          {isUpToDate ? (
            <p className="up-to-date">
              <span className="icon checkmark">&#10003;</span>
              <strong>All required vaccinations are up to date.</strong>
            </p>
          ) : (
            <p className="expired">
              <span className="icon cross">&#10007;</span>
              <strong>Some required vaccinations need attention.</strong>
            </p>
          )}
          {effectiveMissingVaccinations.length > 0 && (
            <p>
              <strong>Missing vaccinations: </strong>
              {effectiveMissingVaccinations
                .filter((vacc, index, self) => {
                  if (vacc.includes('Rabies')) {
                    return self.findIndex(v => v.includes('Rabies')) === index;
                  }
                  return true;
                })
                .map((vacc) => {
                  if (vacc.includes('Rabies')) {
                    return 'Rabies (No valid Initial or Continuing vaccination)';
                  }
                  return vacc;
                }).join(', ')}
            </p>
          )}
          {effectiveExpiredVaccinations.length > 0 && (
            <p>
              <strong>Expired vaccinations: </strong>
              {effectiveExpiredVaccinations.map((vacc, index) => {
                if (vacc === 'Rabies [Continuing]') {
                  return 'Rabies (Both Initial and Continuing are expired)';
                }
                return vacc;
              }).join(', ')}
            </p>
          )}
          {areAllVaccinationsVerified ? (
            <p className="up-to-date">
              <span className="icon checkmark">&#10003;</span>
              <strong>All required vaccinations are verified.</strong>
            </p>
          ) : unverifiedVaccinations.length > 0 ? (
            <>
              <p className="expired">
                <span className="icon cross">&#10007;</span>
                <strong>Some required vaccinations are not verified: </strong>
                {unverifiedVaccinations.map((vacc, index) => {
                  if (vacc === 'Rabies [Continuing]' || vacc === 'Rabies [Initial]') {
                    return 'Rabies';
                  }
                  return vacc;
                }).join(', ')}
              </p>
              <p className="verification-note">
                <small>Please bring copies or email records from your veterinarian to the front desk at the bar to verify your vaccination records.</small>
              </p>
            </>
          ) : null}
        </div>

        {/* Mode toggle: above the vaccines list so users pick an action before viewing/editing the list. */}
        <div className="mode-toggle">
          <button 
            className={`mode-button ${!bulkUpdateMode && !bulkAddMode ? 'active' : ''}`}
            onClick={() => {
              setBulkUpdateMode(false);
              setBulkAddMode(false);
              setBulkReminderMode('none');
              setSharedReminderMonths('');
              setIndividualReminderMonths({});
              setBulkAddReminderMonths('');
              
              // Scroll to single form section after state updates
              setTimeout(() => {
                if (singleFormRef.current && overlayRef.current) {
                  singleFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
          >
            Add/Update Single Vaccine
          </button>
          <button 
            className={`mode-button ${bulkUpdateMode ? 'active' : ''}`}
            onClick={handleBulkUpdateStart}
          >
            Bulk Update Vaccinations
          </button>
          <button 
            className={`mode-button ${bulkAddMode ? 'active' : ''}`}
            onClick={handleBulkAddStart}
          >
            Bulk Add
          </button>
        </div>

        <div className="vaccinations-list">
          <h3>{dog.name}'s Vaccinations:</h3>
          {vaccinations.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Vaccine</th>
                  <th>Date Administered</th>
                  <th>Expiration Date</th>
                  <th>Email Reminder Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {vaccinations
                  .filter((vaccine, index, self) => {
                    // Remove duplicates by keeping only the most recent entry for each vaccine type
                    const firstIndex = self.findIndex(v => v.name === vaccine.name);
                    if (firstIndex === index) {
                      // If this is the first occurrence, keep it
                      return true;
                    }
                    // For subsequent occurrences, only keep if it's more recent
                    const firstVaccine = self[firstIndex];
                    return new Date(vaccine.date) > new Date(firstVaccine.date);
                  })
                  .sort((a, b) => {
                    // Check if either is Rabies [Initial]
                    const aIsInitialRabies = a.name === 'Rabies [Initial]';
                    const bIsInitialRabies = b.name === 'Rabies [Initial]';
                    
                    // If one is Initial Rabies and there's a Continuing Rabies, put Initial last
                    const hasContinuingRabies = vaccinations.some(v => v.name === 'Rabies [Continuing]');
                    if (hasContinuingRabies) {
                      if (aIsInitialRabies && !bIsInitialRabies) return 1;
                      if (!aIsInitialRabies && bIsInitialRabies) return -1;
                    }

                    // Get vaccine types
                    const aVaccineType = RequiredVaccinations.find(v => v.name === a.name);
                    const bVaccineType = RequiredVaccinations.find(v => v.name === b.name);

                    // Sort core vaccines first
                    if (aVaccineType?.core && !bVaccineType?.core) return -1;
                    if (!aVaccineType?.core && bVaccineType?.core) return 1;

                    // Then sort by date administered
                    return new Date(b.date) - new Date(a.date);
                  })
                  .map(vaccine => (
                    <tr key={vaccine._id}>
                      <td>
                        {vaccine.name}
                        {RequiredVaccinations.find(v => v.name === vaccine.name)?.core ? ' (Required)' : ' (Optional)'}
                      </td>
                      <td>{new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(vaccine.date))}</td>
                      <td>{new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(vaccine.expirationDate))}</td>
                      <td>
                        {vaccine.reminderDate ? (
                          <span className="reminder-date-display">
                            {new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(vaccine.reminderDate))}
                          </span>
                        ) : (
                          <span className="no-reminder">None</span>
                        )}
                      </td>
                      <td>{vaccine.verifiedByEmployee ? 'Verified' : 'Pending Verification'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <p>No vaccinations recorded for this dog.</p>
          )}
        </div>

        {bulkUpdateMode && (
          <div ref={bulkUpdateSectionRef} className="bulk-section">
            <div className="form-group">
              <label className="form-label">New Vaccination Date</label>
              <input
                ref={bulkDateInputRef}
                type="date"
                value={newVaccinationDate}
                onChange={(e) => {
                  setNewVaccinationDate(e.target.value);
                  setBulkDateError('');
                }}
                className={`form-input ${bulkDateError ? 'error-input' : ''}`}
              />
              {bulkDateError && (
                <div ref={bulkUpdateErrorRef} className="error-message">
                  {bulkDateError}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Email Reminder Settings - Optional</label>
              <small className="reminder-help-text" style={{display: 'block', marginBottom: '0.5rem'}}>
                We'll send you an email on the reminder date to let you know vaccinations will be expiring soon.
              </small>
              <div className="reminder-mode-toggle">
                <button 
                  className={`reminder-mode-button ${bulkReminderMode === 'none' ? 'active' : ''}`}
                  onClick={() => {
                    setBulkReminderMode('none');
                    setSharedReminderMonths('');
                    setIndividualReminderMonths({});
                  }}
                  type="button"
                >
                  No Reminder
                </button>
                <button 
                  className={`reminder-mode-button ${bulkReminderMode === 'same' ? 'active' : ''}`}
                  onClick={() => {
                    setBulkReminderMode('same');
                    setIndividualReminderMonths({});
                  }}
                  type="button"
                >
                  Same for All
                </button>
                <button 
                  className={`reminder-mode-button ${bulkReminderMode === 'different' ? 'active' : ''}`}
                  onClick={() => {
                    setBulkReminderMode('different');
                    setSharedReminderMonths('');
                  }}
                  type="button"
                >
                  Different for Each
                </button>
              </div>
            </div>

            {bulkReminderMode === 'same' && (
              <div className="form-group">
                <label className="form-label">When to Send Reminder for All Selected Vaccinations</label>
                <select 
                  value={sharedReminderMonths} 
                  onChange={e => setSharedReminderMonths(e.target.value)} 
                  className="form-control reminder-select"
                >
                  <option value="">Select reminder time</option>
                  <option value="1">1 month before expiration</option>
                  <option value="2">2 months before expiration</option>
                  <option value="3">3 months before expiration</option>
                  <option value="6">6 months before expiration</option>
                </select>
                <small className="reminder-help-text">
                  Same reminder time will be set for all selected vaccinations.
                </small>
              </div>
            )}

            {newVaccinationDate && selectedVaccinations.length > 0 && (
              <div className="bulk-expiration-display">
                <strong>Expiration dates for selected vaccinations:</strong>
                <ul>
                  {selectedVaccinations.map(vaccinationId => {
                    const vaccination = vaccinations.find(v => v._id === vaccinationId);
                    const vaccinationType = RequiredVaccinations.find(v => v.name === vaccination.name);
                    if (vaccinationType) {
                      const expirationDate = new Date(newVaccinationDate);
                      expirationDate.setFullYear(expirationDate.getFullYear() + vaccinationType.expiration);
                      return (
                        <li key={vaccinationId}>
                          {vaccination.name.replace(' [Initial]', '').replace(' [Continuing]', '')}: {new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(expirationDate)}
                        </li>
                      );
                    }
                    return null;
                  })}
                </ul>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Select Vaccinations to Update</label>
              <div className="vaccination-selection-list">
                {vaccinations
                  .filter((vaccination, index, self) => {
                    // For bulk update list, only show the most recent entry for each vaccine type
                    const firstIndex = self.findIndex(v => v.name === vaccination.name);
                    if (firstIndex === index) {
                      // If this is the first occurrence, keep it
                      return true;
                    }
                    // For subsequent occurrences, only keep if it's more recent
                    const firstVaccine = self[firstIndex];
                    return new Date(vaccination.date) > new Date(firstVaccine.date);
                  })
                  .sort((a, b) => {
                    const aIsCore = RequiredVaccinations.find(v => v.name === a.name)?.core;
                    const bIsCore = RequiredVaccinations.find(v => v.name === b.name)?.core;
                    if (aIsCore && !bIsCore) return -1;
                    if (!aIsCore && bIsCore) return 1;
                    return 0;
                  })
                  .map(vaccination => (
                    <div key={vaccination._id}>
                      <label className="vaccination-selection-item">
                        <input
                          type="checkbox"
                          checked={selectedVaccinations.includes(vaccination._id)}
                          onChange={() => handleVaccinationSelect(vaccination._id)}
                        />
                        <span>{vaccination.name.replace(' [Initial]', '').replace(' [Continuing]', '')}</span>
                        <span className="vaccination-type">
                          {RequiredVaccinations.find(v => v.name === vaccination.name)?.core ? '(Required)' : '(Optional)'}
                        </span>
                        <span className="vaccination-date">
                          Current administered date: {new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(vaccination.date))},  
                          
                          Expires: {new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(vaccination.expirationDate))}
                        </span>
                      </label>
                      {bulkReminderMode === 'different' && selectedVaccinations.includes(vaccination._id) && (
                        <div className="individual-reminder-select">
                          <label className="reminder-label">Email Reminder:</label>
                          <select 
                            value={individualReminderMonths[vaccination._id] || ''} 
                            onChange={e => setIndividualReminderMonths(prev => ({
                              ...prev,
                              [vaccination._id]: e.target.value
                            }))} 
                            className="form-control reminder-select-small"
                          >
                            <option value="">No reminder</option>
                            <option value="1">1 month before</option>
                            <option value="2">2 months before</option>
                            <option value="3">3 months before</option>
                            <option value="6">6 months before</option>
                          </select>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            <div className="form-button-container">
              <button 
                className="form-button" 
                onClick={handleBulkUpdateSubmit}
                disabled={!newVaccinationDate || selectedVaccinations.length === 0}
              >
                Update Selected Vaccinations
              </button>
              <button className="form-button cancel" onClick={handleBulkUpdateCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {bulkAddMode && (
          <div ref={bulkAddSectionRef} className="bulk-section">
            <div className="form-group">
              <label className="form-label">Vaccination Date</label>
              <input
                ref={bulkAddDateInputRef}
                type="date"
                value={newVaccinationDate}
                onChange={(e) => {
                  setNewVaccinationDate(e.target.value);
                  setBulkDateError('');
                }}
                className={`form-input ${bulkDateError ? 'error-input' : ''}`}
              />
              {bulkDateError && <div ref={bulkAddErrorRef} className="error-message">{bulkDateError}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Email Reminder for All - Optional</label>
              <select 
                value={bulkAddReminderMonths} 
                onChange={e => setBulkAddReminderMonths(e.target.value)} 
                className="form-control reminder-select"
              >
                <option value="">No reminder</option>
                <option value="1">1 month before expiration</option>
                <option value="2">2 months before expiration</option>
                <option value="3">3 months before expiration</option>
                <option value="6">6 months before expiration</option>
              </select>
              <small className="reminder-help-text">
                We'll send you an email on the reminder date for each vaccination. Same reminder time will be set for all selected vaccinations.
              </small>
            </div>

            {newVaccinationDate && selectedVaccinationsToAdd.length > 0 && (
              <div className="bulk-expiration-display">
                <strong>Expiration dates for selected vaccinations:</strong>
                <ul>
                  {selectedVaccinationsToAdd.map(vaccinationName => {
                    let finalVaccinationName = vaccinationName;
                    if (vaccinationName === 'Rabies') {
                      finalVaccinationName = isInitialRabies ? 'Rabies [Initial]' : 'Rabies [Continuing]';
                    }
                    const vaccinationType = RequiredVaccinations.find(v => v.name === finalVaccinationName);
                    if (vaccinationType) {
                      const expirationDate = new Date(newVaccinationDate);
                      expirationDate.setFullYear(expirationDate.getFullYear() + vaccinationType.expiration);
                      return (
                        <li key={vaccinationName}>
                          {vaccinationName}: {new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(expirationDate)}
                        </li>
                      );
                    }
                    return null;
                  })}
                </ul>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Select Vaccinations to Add (Vaccines not listed here are still current and unexpired)</label>
              <div className="vaccination-selection-list">
                {RequiredVaccinations
                  .filter(reqVacc => {
                    // For Rabies, only show if no valid Rabies exists
                    if (reqVacc.name.includes('Rabies')) {
                      const hasValidRabies = vaccinations.some(v => 
                        v.name.includes('Rabies') && isVaccinationUpToDate(v)
                      );
                      // Only show one Rabies option
                      return !hasValidRabies && reqVacc.name === 'Rabies [Initial]';
                    }
                    return !vaccinations.some(v => v.name === reqVacc.name && isVaccinationUpToDate(v));
                  })
                  .sort((a, b) => {
                    if (a.core && !b.core) return -1;
                    if (!a.core && b.core) return 1;
                    return 0;
                  })
                  .map(vaccination => {
                    // For Rabies, show a simplified name
                    const displayName = vaccination.name.includes('Rabies') ? 'Rabies' : vaccination.name;
                    return (
                      <div key={vaccination.name}>
                        <label className="vaccination-selection-item">
                          <input
                            type="checkbox"
                            checked={selectedVaccinationsToAdd.includes(displayName)}
                            onChange={() => handleVaccinationToAddSelect(displayName)}
                          />
                          <span>{displayName}</span>
                          <span className="vaccination-type">
                            {vaccination.core ? '(Required)' : '(Optional)'}
                          </span>
                        </label>
                        {displayName === 'Rabies' && selectedVaccinationsToAdd.includes('Rabies') && (
                          <div className="rabies-type-selection">
                            <label>
                              <input
                                type="radio"
                                checked={isInitialRabies}
                                onChange={() => setIsInitialRabies(true)}
                              />
                              Initial Rabies Vaccine (expires after 1 year)
                            </label>
                            <label>
                              <input
                                type="radio"
                                checked={!isInitialRabies}
                                onChange={() => setIsInitialRabies(false)}
                              />
                              Continuing Rabies Vaccine (expires after 3 years)
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="form-button-container">
              <button 
                className="form-button" 
                onClick={handleBulkAddSubmit}
                disabled={!newVaccinationDate || selectedVaccinationsToAdd.length === 0}
              >
                Add Selected Vaccinations
              </button>
              <button className="form-button cancel" onClick={handleBulkAddCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {!bulkUpdateMode && !bulkAddMode && (
          <form ref={singleFormRef} onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Select Vaccine:</label>
              <select 
                value={selectedVaccine} 
                onChange={e => setSelectedVaccine(e.target.value)} 
                className="form-control"
              >
                <option value="">-- Select a Vaccine --</option>
                {availableVaccines
                  .filter(vaccine => {
                    // For Rabies, only show one option if continuing exists
                    if (vaccine.name.includes('Rabies')) {
                      const hasValidContinuingRabies = vaccinations.some(v => 
                        v.name === 'Rabies [Continuing]' && isVaccinationUpToDate(v)
                      );
                      if (hasValidContinuingRabies) {
                        return vaccine.name === 'Rabies [Continuing]';
                      }
                    }
                    return true;
                  })
                  .sort((a, b) => {
                    if (a.core && !b.core) return -1;
                    if (!a.core && b.core) return 1;
                    return 0;
                  })
                  .map(vaccine => (
                    <option key={vaccine.name} value={vaccine.name}>
                      {vaccine.name} {vaccine.core ? '(Required)' : '(Optional)'}
                    </option>
                  ))}
              </select>
            </div>
            {selectedVaccine && (
              <>
                <div className="form-group">
                  <label>Date Administered:</label>
                  <input 
                    ref={dateInputRef}
                    type="date" 
                    value={date} 
                    onChange={e => {
                      setDate(e.target.value);
                      setDateError('');
                    }}
                    className={`form-input ${dateError ? 'error-input' : ''}`}
                  />
                  {date && selectedVaccine && (() => {
                    const vaccinationType = RequiredVaccinations.find(v => v.name === selectedVaccine);
                    if (vaccinationType) {
                      const expirationDate = new Date(date);
                      expirationDate.setFullYear(expirationDate.getFullYear() + vaccinationType.expiration);
                      return (
                        <div className="expiration-display">
                          <strong>Expiration Date:</strong> {new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(expirationDate)}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {dateError && <div ref={singleVaccineErrorRef} className="error-message">{dateError}</div>}
                </div>
                <div className="form-group">
                  <label>Email Reminder - Optional:</label>
                  <select 
                    value={reminderMonths} 
                    onChange={e => setReminderMonths(e.target.value)} 
                    className="form-control reminder-select"
                  >
                    <option value="">No reminder</option>
                    <option value="1">1 month before expiration</option>
                    <option value="2">2 months before expiration</option>
                    <option value="3">3 months before expiration</option>
                    <option value="6">6 months before expiration</option>
                  </select>
                  <small className="reminder-help-text">
                    We'll send you an email on the reminder date to let you know this vaccination will be expiring soon, so you can schedule a vet appointment to update it.
                  </small>
                </div>
              </>
            )}
            <button type="submit" className="submit-button">Save Vaccination</button>
          </form>
        )}
      </div>
    </div>
  );
}

export default VaccineManagerModal;
